import { useEffect, useState } from "react";
import { useCuentaAtlas } from "../hooks/useCuentaAtlas";
import { SoloElDueno } from "../components/SoloElDueno";
import { PuertaDelPlanDeEmpresa } from "../components/facturacionPorMarca/PuertaDelPlanDeEmpresa";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Loader2,
  AlertCircle,
  CheckCircle2,
  X,
  Ban,
  ImageIcon,
  Video,
  Pencil,
  Sparkles,
  Monitor,
  Headphones,
  TrendingDown,
  Target,
  Clapperboard,
  MessageCircle,
  Users,
  BarChart3,
  BrainCircuit,
  Send,
  ChevronRight,
  ExternalLink,
} from "lucide-react";
import AppleHeader from "../components/AppleHeader";
import { useSidebar } from "../context/SidebarContext";
import { supabase } from "../lib/supabase";
import {
  PAYMENT_CANCEL_URL,
  PAYMENT_PAUSE_URL,
  PAYMENT_REACTIVATE_URL,
  PAYMENT_RESUME_URL,
} from "../config";
import {
  fetchStripeBillingPortalUrl,
  paymentServerFetch,
} from "../lib/paymentServer";
import { gestionaPlanEnPortalStripe } from "../lib/gestionPlan";
import { esPlanDeEquipo } from "../lib/planCapabilities";
import { nombreDelPlanDeEquipo } from "../lib/catalogoDePlanesDeEquipo";
import { PlanesDeEquipo } from "../components/pricing/PlanesDeEquipo";
import { fetchDatosPlanCustom, type MiPlanCustom } from "../lib/planesCustom";
import ContactarPorWhatsApp from "../components/ContactarPorWhatsApp";
import { getPlanById as getCatalogPlan } from "../lib/checkoutPlans";
import { fetchHotSaleCoupon } from "../lib/hotSaleCoupon";
import { Icon } from "../design-system/icons";
import { useUserGroup } from "../hooks/useUserGroup";
import { getCarouselSlidesPorLabel } from "../components/onboarding/onboardingSlides";
import PricingCatalog, {
  type PlanView,
} from "../components/pricing/PricingCatalog";
import { buildPlanViews } from "../components/pricing/buildPlanViews";
// `segundosDeVideoPorCreditos`: el techo de video se dice en segundos (2 créditos por
// segundo), el mismo criterio que el catálogo (ADR-2026-09-24-planes-en-segundos-de-video).
// El campo `videos` de la tabla `ultraTiers` de más abajo quedó obsoleto y no se usa
// para mostrar.
import {
  segundosDeVideoPorCreditos,
  type PricingPlanKey,
} from "../components/pricing/pricingContent";
import { estadoDeCobro, type EstadoDeCobro } from "../lib/estadoDeCobro";
import { leerRechazoDeCobro } from "../lib/cobroRechazado";
import { conSimulacion } from "../lib/simulacionDeCobro";
import { AvisoDeCobro } from "../components/cobros/AvisoDeCobro";
import { avisarCobroActualizado } from "../hooks/useEstadoDeCobro";

// Corte para ofrecer "Reactivar/Reanudar" a clientes de MP: solo si la
// cancelación/pausa se hizo A PARTIR de esta fecha (las hechas por el sistema
// viejo no son reversibles vía el payment server → gateway_error). Rebill no
// tiene corte (todas sus subs son del sistema nuevo). Comparación de strings
// ISO (YYYY-MM-DD...), robusta sin parseo de Date.
const REACTIVACION_MP_DESDE = "2026-07-04";

// Define our subscription plan types
interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  description: string;
  features: string[];
  max_fotos_mes: number;
}

const PaywallDelDueno = () => {
  const navigate = useNavigate();
  const { setMobileTitleOverride, setMobileBackOverride } = useSidebar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /**
   * La carga del contexto de cobro falló — ADR-2026-07-31-subscription-falla-cerrado.
   *
   * Va aparte de `error` porque ese lo comparten los fallos de ACCIÓN (un checkout
   * que no arrancó, una cancelación rechazada), que deben mostrarse **dentro** del
   * catálogo sin vaciarlo. Éste es distinto: sin saber país, plan, plataforma de
   * pago ni `plan_custom_id` no se puede mostrar un catálogo accionable.
   */
  const [cargaFallida, setCargaFallida] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [currentPlan, setCurrentPlan] = useState<string>("");
  // `/subscription?equipos=1`: los planes de equipo, sin el catálogo individual
  // (ADR-2026-10-27-planes-de-equipo-en-la-pantalla-de-planes). Ahí llegan el tope de marcas
  // de un Ultra y el "Ver planes" de la sección Equipo.
  const [parametros] = useSearchParams();
  const verPlanesDeEquipo = parametros.get("equipos") === "1";
  const [processing, setProcessing] = useState(false);
  // Card visible del catálogo rediseñado (selector Básico/Pro/Ultra). Default Pro,
  // que es el plan recomendado. Ver ADR-2026-07-28-rediseno-paywall-catalogo-componentes-presentacionales.
  const [selectedPlanKey, setSelectedPlanKey] =
    useState<PricingPlanKey>("negocio");
  // Switch Mensual/Anual: HOY ES SÓLO VISUAL y sólo lo ven las cuentas
  // `dev_tester`. No hay plan anual en el backend (ni en Rebill/Stripe ni en el
  // catálogo), así que no cambia precios ni lo que se contrata. Ver ADR-2026-07-28-switch-mensual-anual-visual-solo.
  const [annualBilling, setAnnualBilling] = useState<boolean>(false);
  const isDevTester = useUserGroup().isDevTester;
  const [promoMs, setPromoMs] = useState<number>(0);
  const [shortName, setShortName] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");
  const [showPromoCountdown, setShowPromoCountdown] = useState<boolean>(false);
  // Plataforma de pago de la empresa ('rebill' | 'mp'): los endpoints del payment
  // server son multiplataforma, pero el copy (cuándo se debita al reactivar) y el
  // corte de reactivación de MP dependen de esto.
  const [plataformaPago, setPlataformaPago] = useState<string | null>(null);
  // Guard Stripe (ADR-2026-08-24-stripe-pago-no-ve-el-paywall): un cliente de
  // Stripe con plan PAGO no gestiona nada acá — apenas la carga confirma
  // plataforma+plan se lo redirige al Billing Portal. `true` = el portal no se
  // pudo abrir y se muestra el reintento + soporte.
  const [portalStripeError, setPortalStripeError] = useState(false);
  // Plan a medida de esta empresa (ADR-662). Si tiene uno, esta pantalla NO muestra el
  // catálogo: su precio y su cuota se negociaron uno a uno.
  const [miPlanCustom, setMiPlanCustom] = useState<MiPlanCustom | null>(null);
  // `company_profiles.status` ('paused' = pausa hecha por el payment server) y
  // fecha en que se PIDIÓ la cancelación (para el corte de MP).
  const [companyStatus, setCompanyStatus] = useState<string | null>(null);
  const [paidPlanCancelDate, setPaidPlanCancelDate] = useState<string | null>(
    null,
  );
  // Loading de las acciones de reactivación.
  const [reactivating, setReactivating] = useState<boolean>(false);
  const [resuming, setResuming] = useState<boolean>(false);
  // Rebill cobra SINCRÓNICO al reanudar: si la tarjeta rechaza, /resume devuelve
  // `payment_declined` y la suscripción sigue pausada → ofrecemos reintentar o
  // cargar otra tarjeta (checkout embebido).
  const [resumeDeclined, setResumeDeclined] = useState<boolean>(false);

  const [cancellingSubscription, setCancellingSubscription] =
    useState<boolean>(false);
  const [, setCancelSuccess] = useState<boolean>(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  // Flow: 1 (reason) → "pause" (pause offer) → "downgrade" (cheaper-plan
  // offer, only for plans above emprendimiento) → 2 (detail) → 3 (confirm) →
  // 4 (cancel success), OR 1 → "pause" → "paused" (pause success) if the user
  // picks one of the pause buttons.
  const [cancelStep, setCancelStep] = useState<
    1 | "pause" | "downgrade" | 2 | 3 | 4 | "paused"
  >(1);
  const [cancelMotivo, setCancelMotivo] = useState<string>("");
  const [cancelDetalle, setCancelDetalle] = useState<string>("");
  const [cancelSuccessDate, setCancelSuccessDate] = useState<string>("");
  const [pausingSubscription, setPausingSubscription] =
    useState<boolean>(false);
  const [pauseMonthsSelected, setPauseMonthsSelected] = useState<number>(0);
  const [showContactQR, setShowContactQR] = useState<string>("");
  const [fechaCancelacion, setFechaCancelacion] = useState<string | null>(null);
  // Aviso de cobro rechazado (ADR-2026-10-26-cobros-rechazados-en-la-plataforma). Decide qué
  // significa la baja programada: con "baja_programada" es por falta de pago, no pedida.
  const [cobro, setCobro] = useState<EstadoDeCobro | null>(null);
  const [fechaReactivacion, setFechaReactivacion] = useState<string | null>(
    null,
  );

  // Tier de Ultra. Antes era un dropdown con click-outside; en el rediseño lo
  // maneja el slider de la card (ADR-2026-07-28-rediseno-paywall-catalogo-componentes-presentacionales), así que sólo queda el índice.
  const [ultraTierIndex, setUltraTierIndex] = useState<number>(0); // default to 500 images tier
  const ultraTiers = [
    {
      label: "500 imágenes",
      images: 500,
      videos: 40,
      price: 109999,
      planId: "enterprise1",
    },
    {
      label: "1.000 imágenes",
      images: 1000,
      videos: 80,
      price: 209999,
      planId: "enterprise2",
    },
    {
      label: "2.000 imágenes",
      images: 2000,
      videos: 160,
      price: 399999,
      planId: "enterprise3",
    },
  ];

  // Cupón aplicado. Hoy sólo lo setea el efecto del cupón "Hot Sale" (invisible):
  // la carga MANUAL de códigos se sacó de esta pantalla junto con el bloque
  // "Tengo un cupón" (ADR-2026-07-29-iteracion-paywall-pie-minimo-ayuda), y sigue disponible en el checkout.
  const [appliedCoupon, setAppliedCoupon] = useState<{
    id: string;
    codigo: string;
    multiplicador: number;
    es_invisible?: boolean;
  } | null>(null);

  // Override sidebar mobile title so it doesn't show "Estudio virtual"
  useEffect(() => {
    setMobileTitleOverride(" ");
    return () => {
      setMobileTitleOverride(null);
    };
  }, [setMobileTitleOverride]);

  // Country/provider settings
  const [pais, setPais] = useState<string>("");
  /** Label de `company_profiles.rubro`: elige el carrusel lateral del catálogo. */
  const [rubro, setRubro] = useState<string | null>(null);
  const normalizedPais = (pais || "").toLowerCase();
  const isArgentina = normalizedPais === "argentina";
  const isChile = normalizedPais === "chile";
  const isColombia = normalizedPais === "colombia";
  const isMexico = normalizedPais === "méxico" || normalizedPais === "mexico";
  const isPeru = normalizedPais === "perú" || normalizedPais === "peru";

  const currencyCode = isArgentina
    ? "ARS"
    : isChile
      ? "CLP"
      : isColombia
        ? "COP"
        : isMexico
          ? "MXN"
          : isPeru
            ? "PEN"
            : "USD";

  const getPlanPrice = (planId: string, arsPrice: number) => {
    if (isArgentina) return arsPrice;

    if (isChile) {
      switch (planId) {
        case "emprendimiento":
          return 14999;
        case "negocio":
          return 49999;
        case "enterprise":
        case "enterprise1":
          return 79999;
        case "enterprise2":
          return 149999;
        case "enterprise3":
          return 299999;
        default:
          return 0;
      }
    }

    if (isColombia) {
      switch (planId) {
        case "emprendimiento":
          return 59999;
        case "negocio":
          return 199000;
        case "enterprise":
        case "enterprise1":
          return 299999;
        case "enterprise2":
          return 549999;
        case "enterprise3":
          return 1099999;
        default:
          return 0;
      }
    }

    if (isMexico) {
      switch (planId) {
        case "emprendimiento":
          return 299;
        case "negocio":
          return 999;
        case "enterprise":
        case "enterprise1":
          return 1699;
        case "enterprise2":
          return 2999;
        case "enterprise3":
          return 5999;
        default:
          return 0;
      }
    }

    if (isPeru) {
      switch (planId) {
        case "emprendimiento":
          return 49;
        case "negocio":
          return 179;
        case "enterprise":
        case "enterprise1":
          return 299;
        case "enterprise2":
          return 549;
        case "enterprise3":
          return 1099;
        default:
          return 0;
      }
    }

    // Default: USD
    switch (planId) {
      case "emprendimiento":
        return 15;
      case "negocio":
        return 50;
      case "enterprise":
      case "enterprise1":
        return 80;
      case "enterprise2":
        return 150;
      case "enterprise3":
        return 300;
      default:
        return 0;
    }
  };

  const formatPrice = (amount: number) => {
    const locale = isArgentina
      ? "es-AR"
      : isChile
        ? "es-CL"
        : isColombia
          ? "es-CO"
          : isMexico
            ? "es-MX"
            : isPeru
              ? "es-PE"
              : "en-US";

    // USD: precios siempre se muestran como ".99" (valor interno entero - 0.01).
    // Resto de monedas: entero sin decimales (mantienen el comportamiento previo).
    const isUSD = currencyCode === "USD";
    const displayAmount = isUSD && amount > 0 ? amount - 0.01 : amount;
    const fmt = new Intl.NumberFormat(locale, {
      minimumFractionDigits: isUSD ? 2 : 0,
      maximumFractionDigits: isUSD ? 2 : 0,
    });
    return `$${fmt.format(displayAmount)} ${currencyCode}`;
  };

  // Plan hierarchy for determining upgrades / downgrades
  const planOrder: Record<string, number> = {
    gratis: 0,
    emprendimiento: 1,
    negocio: 2,
    enterprise: 3,
    enterprise1: 3.1,
    enterprise2: 3.2,
    enterprise3: 3.3,
    experto: 4,
  };
  // `isUpgrade` vive ahora en `buildPlanViews` (recibe `planOrder`); acá quedó
  // sólo `isDowngrade`, que usa la oferta de retención del flujo de cancelación.
  const isDowngrade = (planId: string) =>
    planOrder[planId] < (planOrder[currentPlan] ?? 0);
  const isEnterprisePlan = (plan: string) =>
    plan === "enterprise" ||
    plan === "enterprise1" ||
    plan === "enterprise2" ||
    plan === "enterprise3";
  // Plans configuration
  const plans: SubscriptionPlan[] = [
    {
      id: "emprendimiento",
      name: "Básico",
      price: 21999,
      description:
        "Pensado para pequeños emprendimientos que están buscando profesionalizar su negocio",
      features: [
        "Hasta 60 imágenes por mes",
        "Función de Edición de imágenes con IA",
        "Acceso desde computadora, tablet y celular",
        "~Generación de videos con IA",
      ],
      max_fotos_mes: 60,
    },
    {
      id: "negocio",
      name: "Pro",
      price: 69999,
      description:
        "Ideal para negocios en crecimiento que quieren imágenes de alta calidad para impulsar sus ventas",
      features: [
        "Hasta 250 imágenes por mes",
        `Hasta ${segundosDeVideoPorCreditos(250).toLocaleString("es-AR")} segundos de video por mes`,
        "Función de Edición de imágenes con IA",
        "Acceso desde computadora, tablet y celular",
        "Soporte prioritario",
      ],
      max_fotos_mes: 250,
    },
    // When user already has an enterprise plan, show individual tier cards
    // Otherwise show a single Ultra card with dropdown selector
    ...(isEnterprisePlan(currentPlan)
      ? ultraTiers.map((tier) => ({
          id: tier.planId,
          name: `Ultra Nivel ${ultraTiers.indexOf(tier) + 1}`,
          price: tier.price,
          description:
            "Creado para negocios con producción de imágenes y gestión catálogos grandes",
          features: [
            `Hasta ${tier.images.toLocaleString("es-AR")} imágenes por mes`,
            `Hasta ${segundosDeVideoPorCreditos(tier.images).toLocaleString("es-AR")} segundos de video por mes`,
            "Función de Edición de imágenes con IA",
            "Acceso desde computadora, tablet y celular",
            "Soporte dedicado 24/7",
            "Mejor relación precio / imagen",
            "Asesoría estratégica para campañas",
          ],
          max_fotos_mes: tier.images,
        }))
      : [
          {
            id: "enterprise",
            name: "Ultra",
            price: ultraTiers[ultraTierIndex].price,
            description:
              "Creado para negocios con producción de imágenes y gestión catálogos grandes",
            features: [
              `Hasta ${ultraTiers[ultraTierIndex].images.toLocaleString("es-AR")} imágenes por mes`,
              `Hasta ${segundosDeVideoPorCreditos(ultraTiers[ultraTierIndex].images).toLocaleString("es-AR")} segundos de video por mes`,
              "Función de Edición de imágenes con IA",
              "Acceso desde computadora, tablet y celular",
              "Soporte dedicado 24/7",
              "Mejor relación precio / imagen",
              "Asesoría estratégica para campañas",
            ],
            max_fotos_mes: ultraTiers[ultraTierIndex].images,
          },
        ]),
    {
      id: "experto",
      name: "Empresa",
      price: 0,
      description: "Dale al marketing de tu empresa contenido IA sin límites",
      features: [
        "Manager de redes dedicado",
        "Estrategia de contenido personalizada",
        "Publicaciones y gestión diaria",
        "Reportes de rendimiento mensuales",
        "Imágenes y videos con IA incluidos",
        "Soporte dedicado 24/7",
      ],
      max_fotos_mes: 0,
    },
  ];

  // Oferta de downgrade dentro del flujo de cancelación (paso "downgrade"):
  // solo para planes superiores a Básico (emprendimiento). Candidatos: todo
  // plan pago más barato que el actual, del más cercano al más chico. El
  // precio base ARS sale del catálogo compartido, que cubre el `enterprise`
  // legacy (no está en las cards cuando el usuario ya es enterprise).
  const planPriceById = (id: string) =>
    getPlanPrice(id, getCatalogPlan(id)?.price ?? 0);
  const downgradeOptions =
    currentPlan !== "experto" &&
    (planOrder[currentPlan] ?? 0) > planOrder.emprendimiento
      ? plans
          .filter(
            (p) =>
              p.id !== "experto" &&
              isDowngrade(p.id) &&
              planPriceById(currentPlan) - planPriceById(p.id) > 0,
          )
          .sort((a, b) => planOrder[b.id] - planOrder[a.id])
      : [];
  const canOfferDowngrade = downgradeOptions.length > 0;

  // Auto-load Hot Sale coupon (todos los países, after profile loads).
  // El cupón se sigue aplicando (descuento activo) pero los elementos visuales
  // con branding "Hot Sale" (banner con logo, fuegos) están ocultos más abajo
  // detrás de la flag HIDE_HOT_SALE_BRANDING.
  // La consulta vive en `lib/hotSaleCoupon` (ADR-2026-07-30-copy-catalogo-segun-contexto-abrio): el sheet de "sin créditos"
  // muestra el mismo catálogo, y con la query duplicada el mismo usuario podía ver
  // dos precios distintos según por dónde entró.
  useEffect(() => {
    if (loading) return;
    fetchHotSaleCoupon().then((cupon) => {
      if (cupon) setAppliedCoupon(cupon);
    });
  }, [loading]);

  // Flag para esconder elementos visuales de "Hot Sale" (banner con logo + fuegos)
  // sin desactivar el descuento. Cuando vuelva a ser una promo activa, poner false.
  const HIDE_HOT_SALE_BRANDING = true;

  useEffect(() => {
    fetchUserProfile();

    // Ensure all desktop cards have the same height
    const timer = setTimeout(() => equalizeCardHeights(), 100);
    const timer2 = setTimeout(() => equalizeCardHeights(), 400);
    const timer3 = setTimeout(() => equalizeCardHeights(), 800);

    window.addEventListener("resize", equalizeCardHeights);

    return () => {
      clearTimeout(timer);
      clearTimeout(timer2);
      clearTimeout(timer3);
      window.removeEventListener("resize", equalizeCardHeights);
    };
  }, []);

  // Abrir en la card del plan vigente (y, si es Ultra, en su tier) en vez del
  // default Pro: un usuario que ya paga entra a ver LO SUYO. Reemplaza al
  // efecto que movía las tabs viejas por índice (ADR-2026-07-28-rediseno-paywall-catalogo-componentes-presentacionales).
  useEffect(() => {
    if (!currentPlan || currentPlan === "gratis") return;
    if (currentPlan === "emprendimiento") setSelectedPlanKey("emprendimiento");
    else if (currentPlan === "negocio") setSelectedPlanKey("negocio");
    else if (isEnterprisePlan(currentPlan)) {
      setSelectedPlanKey("enterprise");
      const tier = ultraTiers.findIndex((t) => t.planId === currentPlan);
      if (tier >= 0) setUltraTierIndex(tier);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlan]);

  // Start a simple 57:00 countdown when page loads (non-persistent across navigations)
  useEffect(() => {
    setPromoMs(57 * 60 * 1000);
    const id = setInterval(() => {
      setPromoMs((prev) => (prev <= 1000 ? 0 : prev - 1000));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Function to ensure all cards (and their internal slots) have equal heights (desktop only).
  // This runs JS-based measurement so it is fully responsive to any viewport width —
  // no brittle fixed min-h values. Called on mount, resize, and when cards expand/collapse.
  const equalizeCardHeights = () => {
    // Only equalize on desktop (md breakpoint = 768px)
    if (window.innerWidth < 768) return;

    // Step 1: Equalize internal slots (title+desc, price, action).
    // This aligns titles, prices, buttons and the HR across cards regardless of text wrapping.
    const slotClasses = ["plan-slot-1", "plan-slot-2", "plan-slot-3"];
    slotClasses.forEach((cls) => {
      const els = document.querySelectorAll(`.${cls}`);
      if (els.length === 0) return;
      els.forEach((el) => {
        (el as HTMLElement).style.minHeight = "0";
      });
      let max = 0;
      els.forEach((el) => {
        const h = (el as HTMLElement).getBoundingClientRect().height;
        if (h > max) max = h;
      });
      const h = Math.ceil(max);
      els.forEach((el) => {
        (el as HTMLElement).style.minHeight = `${h}px`;
      });
    });

    // Note: we intentionally do NOT equalize full card heights.
    // Each card keeps its natural total height so expanding one card's feature list
    // via "Ver más" only grows that card, not the others. Slots 1–3 are still
    // equalized above so title/price/button/HR stay horizontally aligned.
  };

  const featureIcon = (text: string, disabled: boolean) => {
    const cls = `w-4 h-4 ${disabled ? "text-neutral-300" : "text-neutral-600"} mr-1.5 md:mr-2 mt-0.5 flex-shrink-0`;
    if (disabled) return <X className={cls} />;
    if (text.startsWith("Hasta ") && text.includes("imágenes por mes"))
      return <ImageIcon className={cls} />;
    if (text.includes("Videos pro")) return <Clapperboard className={cls} />;
    if (text.includes("Generación de videos")) return <Video className={cls} />;
    if (text.includes("Edición de imágenes")) return <Pencil className={cls} />;
    if (text.includes("Modo Pro")) return <Sparkles className={cls} />;
    if (text.includes("computadora, tablet"))
      return <Monitor className={cls} />;
    if (text.includes("Soporte")) return <Headphones className={cls} />;
    if (text.includes("relación precio"))
      return <TrendingDown className={cls} />;
    if (text.includes("Asesoría")) return <Target className={cls} />;
    if (text.includes("Manager de redes")) return <Users className={cls} />;
    if (text.includes("Estrategia de contenido"))
      return <Target className={cls} />;
    if (text.includes("Publicaciones") && text.includes("gestión"))
      return <Send className={cls} />;
    if (text.includes("Reportes de rendimiento"))
      return <BarChart3 className={cls} />;
    if (text.includes("Imágenes y videos con IA"))
      return <BrainCircuit className={cls} />;
    return <CheckCircle2 className={cls} />;
  };

  // Update mobile nav bar back button behavior based on cancel step
  useEffect(() => {
    if (!showCancelModal) {
      setMobileBackOverride(null);
      return;
    }
    if (cancelStep === 1) {
      setMobileBackOverride(() => () => handleCloseCancelModal());
    } else if (cancelStep === "pause") {
      setMobileBackOverride(() => () => setCancelStep(1));
    } else if (cancelStep === "downgrade") {
      setMobileBackOverride(() => () => setCancelStep("pause"));
    } else if (cancelStep === 2) {
      setMobileBackOverride(() => () => {
        setCancelStep(canOfferDowngrade ? "downgrade" : "pause");
        setCancelDetalle("");
      });
    } else if (cancelStep === 3) {
      setMobileBackOverride(() => () => setCancelStep(2));
    } else if (cancelStep === 4 || cancelStep === "paused") {
      setMobileBackOverride(() => () => {
        handleCloseCancelModal();
        fetchUserProfile();
      });
    }
  }, [showCancelModal, cancelStep]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      setCargaFallida(false);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No se encontró el usuario");

      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("id_company, name_short")
        .eq("id", user.id)
        .single();

      if (profileError) throw profileError;

      if (!profileData.id_company)
        throw new Error("No hay una empresa asociada a este usuario");

      const { data: company, error: companyError } = await supabase
        .from("company_profiles")
        .select(
          "plan, max_fotos_mes, pais, nombre, imagenes_totales_generadas, fecha_cancelacion_programada, fecha_reactivacion_programada, paid_plan_start_date, upgrade_plan_date, trigger_cancelacion, plataforma_pago, status, paid_plan_cancel_date, plan_custom_id, rubro",
        )
        .eq("id_company", profileData.id_company)
        .single();

      if (companyError) throw companyError;

      setProfile({
        ...profileData,
        user_id: user.id,
        __paid_plan_start_date: (company as any)?.paid_plan_start_date || null,
        __upgrade_plan_date: (company as any)?.upgrade_plan_date || null,
      });
      if ((profileData as any)?.name_short)
        setShortName((profileData as any).name_short as string);
      if ((company as any)?.nombre)
        setCompanyName((company as any).nombre as string);

      setCurrentPlan(company.plan);
      // Plan a medida (ADR-662). Quién manda es `plan_custom_id`, que viene en ESTA consulta:
      // si falla, el `throw` de arriba corta y la pantalla muestra error — nunca el catálogo.
      // Se resuelve acá y no en un efecto aparte a propósito: si llegara después, el cliente
      // vería por un instante las cards públicas antes de que las reemplace su panel.
      const planCustomId = (company as any)?.plan_custom_id as string | null | undefined;
      setMiPlanCustom(planCustomId ? await fetchDatosPlanCustom(planCustomId) : null);
      setPlataformaPago(((company as any)?.plataforma_pago as string) || null);
      setCompanyStatus(((company as any)?.status as string) || null);
      setPaidPlanCancelDate(
        ((company as any)?.paid_plan_cancel_date as string) || null,
      );
      if ((company as any)?.pais) setPais((company as any).pais as string);
      // Rubro: elige las slides del carrusel lateral del catálogo
      // (ADR-2026-08-03-catalogo-planes-usa-slides-del-rubro).
      setRubro(((company as any)?.rubro as string) || null);
      // Seteo INCONDICIONAL (con null si no hay): tras reactivar/reanudar y
      // re-fetchear, la pantalla de estado tiene que soltarse.
      setFechaCancelacion(
        ((company as any)?.fecha_cancelacion_programada as string) || null,
      );
      setCobro(
        conSimulacion(estadoDeCobro({
          id_company: profileData.id_company,
          plan: company.plan,
          plataforma_pago: ((company as any)?.plataforma_pago as string) || null,
          trigger_cancelacion: ((company as any)?.trigger_cancelacion as string) || null,
          fecha_cancelacion_programada:
            ((company as any)?.fecha_cancelacion_programada as string) || null,
          paid_plan_cancel_date: ((company as any)?.paid_plan_cancel_date as string) || null,
          ...(company.plan && company.plan !== "gratis"
            ? await leerRechazoDeCobro(profileData.id_company)
            : {}),
        })),
      );
      const reactivacionRaw = (company as any)?.fecha_reactivacion_programada as
        | string
        | null
        | undefined;
      setFechaReactivacion(
        reactivacionRaw && new Date(reactivacionRaw) > new Date()
          ? reactivacionRaw
          : null,
      );

      const total = (company as any)?.imagenes_totales_generadas as
        | number
        | undefined;
      setShowPromoCountdown(typeof total === "number" ? total < 22 : false);
    } catch (err: any) {
      console.error("Error fetching user profile:", err);
      setError(err.message || "Error al cargar la información del usuario");
      setCargaFallida(true);
    } finally {
      setLoading(false);
    }
  };


  const handleSubscription = async (planId: string) => {
    try {
      setProcessing(true);
      setError(null);

      // El fallback al catálogo compartido importa: `plans` cambia de forma según
      // el plan del usuario (con un plan enterprise activo, la card única 'enterprise'
      // se reemplaza por las de cada tier), así que buscar sólo ahí dejaba ids
      // válidos afuera. `getCatalogPlan` los tiene todos.
      const selectedPlan =
        plans.find((plan) => plan.id === planId) ?? getCatalogPlan(planId);
      if (!selectedPlan) throw new Error("Plan no encontrado");

      // For Ultra plan with tier selector, send the specific tier planId
      const actualPlanId =
        planId === "enterprise" ? ultraTiers[ultraTierIndex].planId : planId;

      // Stripe CON suscripción activa: el cambio de plan (upgrade o downgrade)
      // va sí o sí por el Billing Portal. Mandarlo a /checkout crearía una
      // Checkout Session nueva = una SEGUNDA suscripción, y la vieja sigue
      // cobrando (nada la cancela: el webhook solo pisa stripe_subscription_id).
      // El portal hace el swap sobre la sub existente, con prorrateo. Ver ADR-661.
      // Sin sub activa (currentPlan 'gratis') el portal no sirve — no hay nada
      // que cambiar — así que esos van al checkout normal, como siempre.
      if (plataformaPago === "stripe" && currentPlan && currentPlan !== "gratis") {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("Usuario no encontrado");
        // El spinner queda puesto a propósito: el browser se está yendo.
        window.location.href = await fetchStripeBillingPortalUrl(user.id);
        return;
      }

      // Resto (AR/Rebill, MP, o alta nueva sin sub activa): checkout dedicado,
      // pasando el cupón si hay uno aplicado.
      navigate("/checkout", {
        state: {
          planId: actualPlanId,
          pais,
          coupon: appliedCoupon || undefined,
          currentPlan,
        },
      });
      setProcessing(false);
      return;
    } catch (err: any) {
      console.error("Error processing subscription:", err);
      setError(err.message || "Error al procesar la suscripción");
      setProcessing(false);
    }
  };

  // ── Guard Stripe: todo se gestiona en el Billing Portal ────────────────────
  // Un cliente de Stripe con plan pago no debe ver el catálogo NI el flujo de
  // baja/pausa in-app: cambios de plan, tarjeta, facturas y cancelación viven
  // en el portal. El guard corre acá (la página) y no en cada entrada porque
  // /subscription es alcanzable desde muchos lados (upsells de créditos, avisos
  // del sidebar, URL directa) — ver ADR-2026-08-24-stripe-pago-no-ve-el-paywall.
  // El plan a medida queda AFUERA a propósito: su pantalla propia (ADR-662) no
  // muestra catálogo y explica el canal de contacto, portal incluido.
  const esStripePago = gestionaPlanEnPortalStripe(plataformaPago, currentPlan);
  const irAlPortalStripe = async () => {
    try {
      setPortalStripeError(false);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuario no encontrado");
      // El spinner queda puesto a propósito: el browser se está yendo.
      window.location.href = await fetchStripeBillingPortalUrl(user.id);
    } catch (err) {
      console.error("Error abriendo el Billing Portal:", err);
      setPortalStripeError(true);
    }
  };
  useEffect(() => {
    if (loading || cargaFallida || miPlanCustom) return;
    // Un plan de equipo tiene su propio panel, y la vista de planes de equipo la tiene que
    // poder ver también quien paga por Stripe: ninguno de los dos se va al portal solo.
    if (esPlanDeEquipo(currentPlan) || verPlanesDeEquipo) return;
    if (!esStripePago) return;
    void irAlPortalStripe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, cargaFallida, esStripePago, !!miPlanCustom, currentPlan, verPlanesDeEquipo]);

  // Cancel flow reasons
  const cancelReasons = [
    "La calidad de las imágenes no cumplió mis expectativas",
    "La calidad de los videos no cumplió mis expectativas",
    "Quiero pausar mi suscripción por ahora",
    "El plan es demasiado caro",
    "Ya no necesito crear imágenes",
    "Ya no necesito crear videos",
    "Me cambié a otra herramienta",
    "La herramienta es muy complicada para mí",
    "Otro",
  ];

  // Opens the cancel confirmation modal
  const handleOpenCancelModal = () => {
    // Defensa en profundidad: un Stripe pago nunca debería llegar acá (el guard
    // de la página redirige antes de renderizar el botón), pero si una carrera
    // lo dejó pasar, va al portal igual — la baja de Stripe ya no se hace in-app.
    if (esStripePago) {
      void irAlPortalStripe();
      return;
    }
    setCancelStep(1);
    setCancelMotivo("");
    setCancelDetalle("");
    setCancelSuccessDate("");
    setShowCancelModal(true);
    setMobileBackOverride(() => () => handleCloseCancelModal());
  };

  // Close the cancel confirmation modal
  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
    setCancelStep(1);
    setCancelMotivo("");
    setCancelDetalle("");
    setPauseMonthsSelected(0);
    setMobileBackOverride(null);
  };

  // Calculate next renewal date for the success message
  const getNextRenewalDate = (): string => {
    const paidStart = (profile as any)?.__paid_plan_start_date;
    const upgradeDate = (profile as any)?.__upgrade_plan_date;
    if (!paidStart) {
      const now = new Date();
      return new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        1,
      ).toLocaleDateString("es-AR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
    const now = new Date();
    const subscriptionDate = new Date(paidStart);
    const referenceDate = upgradeDate
      ? new Date(upgradeDate)
      : subscriptionDate;
    const anchorDay = referenceDate.getDate();
    const daysInMonth = (year: number, month: number) =>
      new Date(year, month + 1, 0).getDate();
    const anchorFor = (year: number, month: number) => {
      const dim = daysInMonth(year, month);
      const day = Math.min(anchorDay, dim);
      return new Date(year, month, day, 0, 0, 0, 0);
    };
    let start = anchorFor(now.getFullYear(), now.getMonth());
    if (start.getTime() > now.getTime()) {
      const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      start = anchorFor(prevMonth.getFullYear(), prevMonth.getMonth());
    }
    if (start.getTime() < subscriptionDate.getTime()) {
      start = new Date(
        subscriptionDate.getFullYear(),
        subscriptionDate.getMonth(),
        subscriptionDate.getDate(),
        0,
        0,
        0,
        0,
      );
    }
    const nextMonth = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    const end = anchorFor(nextMonth.getFullYear(), nextMonth.getMonth());
    return end.toLocaleDateString("es-AR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Handler for cancel subscription button after confirmation
  const handleCancelSubscription = async () => {
    try {
      setCancellingSubscription(true);
      setError(null);

      // Get the current user's ID
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuario no encontrado");

      if (plataformaPago === "mp" || plataformaPago === "rebill") {
        // Payment server: soporta MP y Rebill (resuelve la plataforma solo).
        // Programa la baja a fin de período y devuelve `scheduledFor`.
        const response = await paymentServerFetch(PAYMENT_CANCEL_URL, {
          method: "POST",
          body: JSON.stringify({
            user_id: user.id,
            motivo: cancelMotivo,
            detalle: cancelDetalle,
          }),
        });
        if (!response.ok) throw new Error("Error al cancelar la suscripción");
        const data = await response.json();
        if (data?.ok === true) {
          setCancelSuccessDate(
            data.scheduledFor
              ? new Date(data.scheduledFor).toLocaleDateString("es-AR", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })
              : getNextRenewalDate(),
          );
          setCancelStep(4);
          setCancelSuccess(true);
        } else {
          throw new Error("Error al procesar la cancelación");
        }
      } else {
        // Stripe ya no cancela in-app: el guard de la página lo redirige al
        // Billing Portal antes de llegar acá, y el webhook n8n que atendía esta
        // rama quedó retirado (ADR-2026-08-24-stripe-pago-no-ve-el-paywall).
        // Cualquier plataforma desconocida cae al camino de soporte del catch.
        throw new Error(
          `plataforma de pago sin cancelación in-app: ${plataformaPago ?? "desconocida"}`,
        );
      }
    } catch (err: any) {
      console.error("Error cancelling subscription:", err);
      // Si la cancelación falla (p. ej. el preapproval ya estaba `cancelled` en
      // MercadoPago → "You can not modify a cancelled preapproval"), no dejamos
      // al usuario con un error suelto: cerramos el flujo y abrimos el contacto
      // por WhatsApp (mismo patrón que el resto de la app) para que verifiquen
      // el estado de su cancelación con soporte.
      setShowCancelModal(false);
      setShowContactQR(
        `Buenas, soy ${shortName || ""} de ${companyName || ""}. Intenté cancelar mi suscripción desde la app pero me dio un error. ¿Podrían verificar el estado de mi cancelación?`,
      );
    } finally {
      setCancellingSubscription(false);
    }
  };

  // Handler for the pause subscription buttons (1 or 2 months). Va al payment
  // server (multiplataforma): pausa la suscripción y la reanuda sola en
  // `resume_at`. Durante la pausa no se cobra y el plan queda congelado.
  const handlePauseSubscription = async (months: 1 | 2) => {
    if (pausingSubscription) return;
    try {
      setPausingSubscription(true);
      setPauseMonthsSelected(months);
      setError(null);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuario no encontrado");

      if (plataformaPago === "mp" || plataformaPago === "rebill") {
        const resumeAt = new Date(
          Date.now() + months * 30 * 24 * 60 * 60 * 1000,
        ).toISOString();
        const response = await paymentServerFetch(PAYMENT_PAUSE_URL, {
          method: "POST",
          body: JSON.stringify({ user_id: user.id, resume_at: resumeAt }),
        });

        if (!response.ok) throw new Error("Error al pausar la suscripción");
        const data = await response.json();
        if (data?.error === "pending_cancellation") {
          // Ya hay una baja en curso (estado local desactualizado): cerramos el
          // flujo y refrescamos → se muestra la pantalla de cancelación programada
          // (con su botón de "Reactivar"), que es la acción que corresponde.
          setShowCancelModal(false);
          await fetchUserProfile();
          return;
        }
        if (data?.ok !== true) throw new Error(data?.error || "pause_failed");
      } else {
        // Stripe ya no pausa in-app (mismo motivo que la cancelación — el guard
        // lo manda al portal antes de llegar acá). Trade-off aceptado al retirar
        // el webhook: el portal de Stripe no ofrece pausa, así que esos clientes
        // pierden la oferta de "pausá 1-2 meses" — decisión de Matías 2026-08-24.
        throw new Error(
          `plataforma de pago sin pausa in-app: ${plataformaPago ?? "desconocida"}`,
        );
      }

      setCancelStep("paused");
    } catch (err: any) {
      console.error("Error pausing subscription:", err);
      // Mismo manejo que la cancelación: ante un error, cerramos el flujo y
      // ofrecemos contactar a soporte por WhatsApp para verificar el estado.
      setShowCancelModal(false);
      setShowContactQR(
        `Buenas, soy ${shortName || ""} de ${companyName || ""}. Intenté pausar mi suscripción desde la app pero me dio un error. ¿Podrían verificar el estado de mi suscripción?`,
      );
    } finally {
      setPausingSubscription(false);
    }
  };

  // Deshacer una cancelación programada (POST /reactivate). No se cobra nada en
  // el momento (el ciclo ya estaba pago); el próximo débito es en la fecha que
  // era de baja. Al OK, re-fetcheamos el perfil → la pantalla de "Cancelación
  // programada" se suelta sola.
  const handleReactivateSubscription = async () => {
    if (reactivating) return;
    try {
      setReactivating(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuario no encontrado");

      const response = await paymentServerFetch(PAYMENT_REACTIVATE_URL, {
        method: "POST",
        body: JSON.stringify({ user_id: user.id }),
      });
      if (!response.ok) throw new Error("Error al reactivar");
      const data = await response.json();
      if (data?.ok === true || data?.error === "no_pending_cancellation") {
        // `no_pending_cancellation` = nuestro estado estaba viejo (ya no hay
        // baja en curso) → refrescar alcanza.
        await fetchUserProfile();
      } else {
        throw new Error(data?.error || "reactivate_failed");
      }
    } catch (err: any) {
      console.error("Error reactivating subscription:", err);
      setShowContactQR(
        `Buenas, soy ${shortName || ""} de ${companyName || ""}. Intenté reactivar mi suscripción desde la app pero me dio un error. ¿Podrían ayudarme a reactivarla?`,
      );
    } finally {
      setReactivating(false);
    }
  };

  // Reanudar una pausa voluntaria (POST /resume). Rebill: cobra al reanudar
  // (ciclo nuevo desde hoy). MP: reactiva ya y cobra en su próxima fecha de
  // facturación. Al OK, re-fetcheamos el perfil.
  const handleResumeSubscription = async () => {
    if (resuming) return;
    try {
      setResuming(true);
      setResumeDeclined(false);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuario no encontrado");

      const response = await paymentServerFetch(PAYMENT_RESUME_URL, {
        method: "POST",
        body: JSON.stringify({ user_id: user.id }),
      });
      if (!response.ok) throw new Error("Error al reanudar");
      const data = await response.json();
      if (data?.error === "payment_declined") {
        // Rebill rechazó el cobro sincrónico: sigue pausada. Inline (no WhatsApp):
        // reintentar o cargar otra tarjeta.
        setResumeDeclined(true);
        return;
      }
      if (data?.ok !== true) throw new Error(data?.error || "resume_failed");
      await fetchUserProfile();
      // La sidebar del shell no se remonta: que vuelva a leer el cobro (deja de decir "Pago rechazado").
      avisarCobroActualizado();
    } catch (err: any) {
      console.error("Error resuming subscription:", err);
      setShowContactQR(
        `Buenas, soy ${shortName || ""} de ${companyName || ""}. Intenté reanudar mi suscripción pausada desde la app pero me dio un error. ¿Podrían ayudarme a reanudarla?`,
      );
    } finally {
      setResuming(false);
    }
  };

  // Mensaje de la ayuda por WhatsApp. Uno solo para las tres entradas (botón del
  // pie, flotante de arriba y modal con QR), así no se desincronizan.
  const dudaMensaje = `Buenas, soy ${shortName || ""} de ${companyName || ""}. Me contactaba porque tenia una duda.`;

  // ── Catálogo rediseñado (ADR-2026-07-28-rediseno-paywall-catalogo-componentes-presentacionales) ──────────────────────────────────────────
  // `PricingCatalog` es puramente presentacional: `buildPlanViews` resuelve acá
  // TODO lo que depende del usuario (país → moneda y tabla de precios, cupón,
  // plan vigente, tier de Ultra) y se lo pasa ya formateado. La lógica de precios
  // sigue siendo la misma de siempre: se le inyectan las funciones inline de esta
  // página, sin tocarlas.
  //
  // El plan vigente y el cupón se pisan SÓLO acá (no en toda la página) cuando hay
  // overrides de dev: así el catálogo se ve como el del usuario simulado, pero los
  // flujos de baja/pausa siguen operando sobre la suscripción REAL — que es lo que
  // uno espera si igual toca "Cancelar".
  const planViews: PlanView[] = buildPlanViews({
    currentPlan,
    ultraTiers,
    ultraTierIndex,
    coupon: appliedCoupon,
    // Ya fue cliente pago ⇒ no vuelve a ver la promo de captación aunque hoy
    // figure en `gratis` (ADR-2026-08-18-descuento-no-revive-al-volver-a-gratis).
    // El dato ya venía en la consulta de la empresa; acá solo se lee.
    yaFueClientePago: !!(profile as any)?.__paid_plan_start_date,
    esArgentina: isArgentina,
    arsBaseFor: (key) => getCatalogPlan(key)?.price ?? 0,
    getPlanPrice,
    formatPrice,
    planOrder,
    processing,
    onCancel: handleOpenCancelModal,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  // ── Falla cerrado: sin contexto de cobro no se muestra catálogo ──
  // ADR-2026-07-31-subscription-falla-cerrado. Antes, si la consulta de
  // `profiles`/`company_profiles` fallaba, la pantalla seguía de largo y renderizaba
  // el catálogo COMPLETO Y ACCIONABLE con los estados en su valor inicial. Eso
  // rompía tres cosas a la vez:
  //  · `miPlanCustom` en `null` → el guard de plan a medida de acá abajo no
  //    disparaba y un cliente con precio negociado veía las cards públicas (ADR-662/664).
  //  · `plataformaPago` en `null` y `currentPlan` en `""` → la guarda de Stripe de
  //    `handleSubscription` no se cumplía y un cliente con suscripción activa iba al
  //    checkout en vez del Billing Portal: SEGUNDA suscripción con la vieja
  //    cobrando (ADR-661).
  //  · `pais` en `""` → `Checkout` hace `state?.pais || "Argentina"`, así que a
  //    cualquiera del exterior le ofrecía Mercado Pago y precios en ARS.
  if (cargaFallida) {
    return (
      <div className="px-4 py-2 md:p-8 w-full max-w-full overflow-x-hidden">
        <div className="sm:hidden">
          <AppleHeader title="Mi plan" />
        </div>
        <h1 className="hidden sm:block text-[22px] font-semibold text-brand-black/90 mb-6">
          Mi plan
        </h1>
        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700 mx-1">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>
            No pudimos cargar los datos de tu plan. Recargá la página; si sigue
            pasando, escribinos y lo vemos.
          </span>
        </div>
        <div className="mx-1 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => fetchUserProfile()}
            className="inline-flex h-10 items-center justify-center rounded-md bg-brand-primary px-4 text-[15px] font-medium text-white shadow-sm transition-colors hover:bg-brand-primary-hover"
          >
            Reintentar
          </button>
          <ContactarPorWhatsApp
            mensaje="Buenas, no me carga la pantalla de mi plan y quería ver mis opciones."
            tituloQR="Hablemos de tu plan"
          >
            Escribinos
          </ContactarPorWhatsApp>
        </div>
      </div>
    );
  }

  // ── Cliente con plan a medida: no ve el catálogo (ADR-662) ──
  // Su precio y su cuota se negociaron uno a uno; mostrarle las cards públicas lo invitaría a
  // "cambiarse" a un plan más chico y más caro que el suyo, o a generar una segunda
  // suscripción. Ve su plan y el portal de Stripe, que es donde puede tocar tarjeta,
  // facturas y baja.
  //
  // Esto además tapa un bug real que ya existía: `planOrder` no tiene entrada para
  // 'enterprise_plus', así que `planOrder[currentPlan] ?? 0` daba 0 y TODAS las cards le
  // aparecían como "Mejorar plan".
  if (miPlanCustom) {
    return (
      <div className="px-4 py-2 md:p-8 w-full max-w-full overflow-x-hidden">
        <div className="sm:hidden">
          <AppleHeader title="Mi plan" />
        </div>
        <h1 className="hidden sm:block text-[22px] font-semibold text-brand-black/90 mb-6">
          Mi plan
        </h1>

        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700 mx-1">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="bg-surface-card rounded-lg border border-neutral-200 p-5 max-w-xl">
          <p className="text-[15px] text-neutral-500">Plan actual</p>
          <p className="text-[22px] font-semibold text-brand-primary mb-3">
            {miPlanCustom.nombre}
          </p>
          {miPlanCustom.maxFotosMes !== null && (
            <p className="text-[15px] text-neutral-600 mb-1">
              {miPlanCustom.maxFotosMes.toLocaleString("es-AR")} créditos por mes
            </p>
          )}
          <p className="text-[13px] text-neutral-500 mb-5">
            Es un plan armado para tu empresa. Cualquier cambio —la cuota, el precio, la
            facturación o la tarjeta— lo vemos con vos por WhatsApp.
          </p>
          <ContactarPorWhatsApp
            mensaje={`Buenas, soy ${shortName || ""} de ${companyName || ""}. Me contactaba por mi ${miPlanCustom.nombre}.`}
            tituloQR="Hablemos de tu plan"
          >
            Hablar con Atlas
          </ContactarPorWhatsApp>
        </div>
      </div>
    );
  }

  // ── Planes de equipo: `/subscription?equipos=1`, o la pantalla de plan de quien ya tiene uno ──
  // ADR-2026-10-27-planes-de-equipo-en-la-pantalla-de-planes-2. Las cards del catálogo
  // individual con los datos de la landing (precio por país, Agencias del 2.000 al 9.000).
  // Quien ya tiene un plan de equipo nunca ve el catálogo individual: ninguna card es la
  // suya, así que todas le aparecían como "Suscribirme" —una "mejora" a un plan más chico
  // que crearía una segunda suscripción— y no tenía cómo darse de baja. Ve su plan marcado
  // "Tu plan" y habla con Atlas. Va antes que el portal de Stripe.
  if (verPlanesDeEquipo || esPlanDeEquipo(currentPlan)) {
    const tienePlanDeEquipo = esPlanDeEquipo(currentPlan);
    const hayAviso = !!error || portalStripeError || (tienePlanDeEquipo && esStripePago);
    // Portaleado a <body> como el catálogo de los planes individuales: es el MISMO
    // `PricingCatalog` (pantalla oscura con carrusel en el teléfono, la ventana de dos paneles
    // en la compu), que tapa la página entera.
    return createPortal(
      <PlanesDeEquipo
        planActual={currentPlan}
        pais={pais || null}
        onClose={() => navigate(-1)}
        onVerPlanesIndividuales={tienePlanDeEquipo ? undefined : () => navigate("/subscription")}
        aviso={
          hayAviso ? (
            <div className="flex flex-col items-center gap-3">
              {(error || portalStripeError) && (
                <div className="w-full p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {error ||
                      "No pudimos abrir el portal de Stripe. Probá de nuevo; si sigue pasando, escribinos."}
                  </span>
                </div>
              )}
              {tienePlanDeEquipo && esStripePago && (
                <button
                  type="button"
                  onClick={() => void irAlPortalStripe()}
                  className="inline-flex h-10 items-center justify-center rounded-md border border-neutral-200 bg-white px-4 text-[15px] font-medium text-brand-black/90 shadow-sm transition-colors hover:bg-neutral-50"
                >
                  Abrir portal de Stripe
                </button>
              )}
            </div>
          ) : undefined
        }
      />,
      document.body,
    );
  }

  // ── Cliente de Stripe con plan pago: no ve esta pantalla ──
  // El effect de arriba ya disparó la redirección al Billing Portal; esto es lo
  // que se muestra mientras el browser se va (o si el portal no se pudo abrir).
  if (esStripePago) {
    return (
      <div className="px-4 py-2 md:p-8 w-full max-w-full overflow-x-hidden">
        <div className="sm:hidden">
          <AppleHeader title="Mi plan" />
        </div>
        <h1 className="hidden sm:block text-[22px] font-semibold text-brand-black/90 mb-6">
          Mi plan
        </h1>
        <div className="bg-surface-card rounded-lg border border-neutral-200 p-5 max-w-xl">
          {portalStripeError ? (
            <>
              <p className="text-[15px] text-brand-black/90 mb-4">
                Tu plan se gestiona desde el portal de facturación de Stripe,
                pero no pudimos abrirlo. Probá de nuevo; si sigue pasando,
                escribinos.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => void irAlPortalStripe()}
                  className="inline-flex h-10 items-center justify-center rounded-md bg-brand-primary px-4 text-[15px] font-medium text-white shadow-sm transition-colors hover:bg-brand-primary-hover"
                >
                  Abrir portal de Stripe
                </button>
                <ContactarPorWhatsApp
                  mensaje={`Buenas, soy ${shortName || ""} de ${companyName || ""}. No puedo abrir el portal de facturación para gestionar mi plan.`}
                  tituloQR="Hablemos de tu plan"
                >
                  Escribinos
                </ContactarPorWhatsApp>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-3 text-[15px] text-brand-black/90">
              <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
              <span>
                Te estamos llevando al portal de Stripe para gestionar tu plan…
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-2 md:p-8 w-full max-w-full overflow-x-hidden">
      {/* Mobile: title only, no back button (sidebar provides it) */}
      <div className="sm:hidden">
        <AppleHeader
          title={
            fechaCancelacion || fechaReactivacion
              ? "Tu plan"
              : currentPlan !== "gratis"
                ? "Mejora tu plan"
                : "Elegí tu plan"
          }
        />
      </div>
      {/* Desktop: title + back button */}
      <div className="hidden sm:block relative">
        <AppleHeader
          title={
            fechaCancelacion || fechaReactivacion
              ? "Tu plan"
              : currentPlan !== "gratis"
                ? "Mejora tu plan"
                : "Elegí tu plan"
          }
          onBack={() => navigate(-1)}
          backLabel="Volver"
        />
      </div>
      {!fechaCancelacion && !fechaReactivacion && (
        <p className="text-[13px] text-neutral-500 text-center -mt-2 mb-4 sm:mb-5">
          Aumenta tus ventas como ya lo hicieron cientos de marcas
        </p>
      )}
      {/* Cobro rechazado con reintento (A) o plan dado de baja por falta de pago (C), arriba
          del catálogo. La baja programada (B) va dentro de la tarjeta de "Tu plan". */}
      {/* La baja programada simulada (TEMPORAL, lib/simulacionDeCobro) no tiene la tarjeta de
          "Tu plan" que la contiene: se muestra acá arriba. */}
      {cobro &&
        (cobro.tipo !== "baja_programada" || cobro.simulado) &&
        !fechaCancelacion &&
        !fechaReactivacion && (
        <AvisoDeCobro estado={cobro} enSuscripcion className="max-w-[420px] mx-auto mb-4 sm:mb-5" />
      )}

      {/* Hot Sale banner — desktop */}
      {!HIDE_HOT_SALE_BRANDING && !fechaCancelacion && !fechaReactivacion && appliedCoupon && appliedCoupon.es_invisible && (
        <div className="hidden md:flex items-center justify-center gap-8 mx-auto max-w-lg mb-0 relative z-20">
          <img
            src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/hot%20sale%20logo.png"
            alt="Hot Sale"
            className="h-32 object-contain"
          />
          <div className="text-center">
            <p
              className="text-[56px] font-bold leading-none"
              style={{ color: "#E55D35" }}
            >
              {Math.round((1 - appliedCoupon.multiplicador) * 100)}%
            </p>
            <p className="text-[17px] font-medium text-neutral-600">
              de descuento
            </p>
          </div>
        </div>
      )}
      {/* Fire row — desktop only, tucked behind banner (above) and cards (below) */}
      {!HIDE_HOT_SALE_BRANDING && !fechaCancelacion && !fechaReactivacion && appliedCoupon?.es_invisible && (
        <div className="hidden md:flex justify-center pointer-events-none -mt-8 -mb-16 relative z-0">
          {[...Array(7)].map((_, i) => (
            <img
              key={i}
              src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/hot%20sale%20fuego.png"
              alt=""
              className="h-48 object-contain -mx-2 opacity-30"
            />
          ))}
        </div>
      )}
      {/* Hot Sale banner — mobile */}
      {!HIDE_HOT_SALE_BRANDING && !fechaCancelacion && !fechaReactivacion && appliedCoupon && appliedCoupon.es_invisible && (
        <div className="mx-auto max-w-lg mb-2 md:hidden">
          <div className="flex items-center justify-center gap-3">
            <img
              src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/hot%20sale%20logo.png"
              alt="Hot Sale"
              className="h-28 object-contain"
            />
            <div className="text-center">
              <p
                className="text-[48px] font-bold leading-none"
                style={{ color: "#E55D35" }}
              >
                {Math.round((1 - appliedCoupon.multiplicador) * 100)}%
              </p>
              <p className="text-[17px] font-medium text-neutral-600">
                de descuento
              </p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700 mx-1">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2.5 text-[15px] text-green-700 mx-1">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* ── Scheduled cancellation notice ── */}
      {fechaCancelacion &&
        (() => {
          const currentPlanObj = plans.find((p) => p.id === currentPlan);
          const CANCEL_PLAN_LABELS: Record<string, string> = {
            gratis: "Prueba",
            emprendimiento: "Básico",
            negocio: "Pro",
            enterprise: "Ultra",
            enterprise1: "Ultra 500",
            enterprise2: "Ultra 1.000",
            enterprise3: "Ultra 2.000",
            experto: "Empresa",
          };
          const planLabel = CANCEL_PLAN_LABELS[currentPlan] || nombreDelPlanDeEquipo(currentPlan) || currentPlan;
          const cancelDateStr = new Date(fechaCancelacion).toLocaleDateString(
            "es-AR",
            { day: "numeric", month: "long", year: "numeric" },
          );
          // Baja por falta de pago (cualquier motivo de cobro fallido, de los tres
          // procesadores): no es la baja pedida, así que no se ofrece "Reactivar sin
          // cobrar" — se regulariza pagando (ADR-2026-10-26-cobros-rechazados-en-la-plataforma).
          // `estadoDeCobro` la sigue dando como "baja_programada" aunque la fecha haya vencido y
          // el cron todavía no corrió: en esa hora tampoco se ofrece "Reactivar".
          const byPaymentFailure = cobro?.tipo === "baja_programada";
          // Reactivación (deshacer la baja): Rebill siempre; MP solo si la
          // cancelación se PIDIÓ desde el corte (las viejas, hechas por el
          // sistema anterior, no son reversibles → gateway_error). Stripe (u
          // otra) NO — el payment server no la soporta. Excluimos las bajas por
          // rechazo de pago (las restaura el reintento automático).
          const canReactivate =
            !byPaymentFailure &&
            (plataformaPago === "rebill" ||
              (plataformaPago === "mp" &&
                !!paidPlanCancelDate &&
                paidPlanCancelDate.slice(0, 10) >= REACTIVACION_MP_DESDE));
          const planPrice = currentPlanObj
            ? formatPrice(
                getPlanPrice(currentPlanObj.id, currentPlanObj.price),
              )
            : null;
          return (
            <div className="max-w-[420px] mx-auto mt-2">
              {byPaymentFailure && cobro && (
                <AvisoDeCobro
                  estado={cobro}
                  enSuscripcion
                  className="mb-3"
                  onPagarAhora={handleResumeSubscription}
                  pagando={resuming}
                  aviso={
                    resumeDeclined
                      ? "El pago volvió a rechazarse. Cambiá la tarjeta y volvé a tocar Pagar ahora."
                      : null
                  }
                />
              )}
              {/* Grouped card container */}
              <div className="bg-surface-card rounded-[13px] overflow-hidden">
                {/* Plan header row */}
                <div className="px-4 py-3.5 flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-sm bg-brand-primary/10 flex items-center justify-center">
                    <span className="material-symbols-rounded text-brand-primary text-[17px]">
                      workspace_premium
                    </span>
                  </div>
                  <p className="text-[15px] font-semibold text-brand-black/90">
                    Plan {planLabel}
                  </p>
                </div>

                {/* Separator */}
                <div className="mx-4 h-px bg-black/[0.06]" />

                {/* Cancellation info */}
                <div className="px-4 py-3.5">
                  <div className="flex items-start gap-2.5">
                    <div className="w-9 h-9 rounded-sm bg-amber-50 flex items-center justify-center shrink-0">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                    </div>
                    <div className="pt-0.5">
                      <p className="text-[15px] font-medium text-brand-black/90">
                        Cancelación programada
                      </p>
                      <p className="text-[13px] text-neutral-500 mt-0.5">
                        Tu plan se cancelará el{" "}
                        <strong className="text-brand-black/80">
                          {cancelDateStr}
                        </strong>
                        {byPaymentFailure && " porque no pudimos cobrarlo"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Separator */}
                <div className="mx-4 h-px bg-black/[0.06]" />

                {/* Info note */}
                <div className="px-4 py-3.5">
                  {byPaymentFailure ? (
                    <p className="text-[13px] text-neutral-500 leading-relaxed">
                      Hasta esa fecha seguís teniendo acceso a todas las
                      funciones de tu plan.{" "}
                      <span className="text-brand-black/80 font-medium">
                        Si el cobro entra antes, tu plan sigue como siempre.
                      </span>
                    </p>
                  ) : (
                    <p className="text-[13px] text-neutral-500 leading-relaxed">
                      Hasta esa fecha seguirás teniendo acceso a todas las
                      funciones de tu plan actual.{" "}
                      <span className="text-brand-black/80 font-medium">
                        No se te realizará ningún cobro adicional.
                      </span>
                    </p>
                  )}
                </div>

                {/* Reactivar (deshacer la cancelación) */}
                {canReactivate && (
                  <>
                    <div className="mx-4 h-px bg-black/[0.06]" />
                    <div className="px-4 py-3.5">
                      <p className="text-[13px] text-neutral-500 leading-relaxed mb-3">
                        ¿Te arrepentiste? Podés reactivar tu plan hasta esa
                        fecha. No se te cobra nada ahora:{" "}
                        <span className="text-brand-black/80 font-medium">
                          {planPrice
                            ? `se te volverá a debitar ${planPrice} el ${cancelDateStr}`
                            : `el próximo débito será el ${cancelDateStr}`}
                        </span>
                        , tu próxima fecha de facturación.
                      </p>
                      <button
                        type="button"
                        onClick={handleReactivateSubscription}
                        disabled={reactivating}
                        className="w-full py-2.5 px-5 bg-brand-primary hover:bg-brand-primary-hover text-white text-[14px] font-medium rounded-md shadow-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                      >
                        {reactivating ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />{" "}
                            Reactivando…
                          </>
                        ) : (
                          "Reactivar suscripción"
                        )}
                      </button>
                    </div>
                  </>
                )}

                {/* Features (only if paid plan) */}
                {currentPlanObj && (
                  <>
                    <div className="mx-4 h-px bg-black/[0.06]" />
                    <div className="px-4 py-3.5">
                      <p className="text-[11px] font-medium text-neutral-400 uppercase tracking-wider mb-2.5">
                        Tu plan incluye
                      </p>
                      <ul className="space-y-2">
                        {currentPlanObj.features
                          .filter((f) => !f.startsWith("~"))
                          .map((feature, i) => (
                            <li
                              key={i}
                              className="flex items-start text-[13px] text-neutral-600"
                            >
                              {featureIcon(feature, false)}
                              <span>{feature}</span>
                            </li>
                          ))}
                      </ul>
                    </div>
                  </>
                )}
              </div>

              {/* WhatsApp help — mobile: direct link, desktop: QR modal */}
              <div className="mt-4 text-center">
                <a
                  href={`https://wa.me/5491122635392?text=${encodeURIComponent(`Buenas, soy ${shortName || ""} de ${companyName || ""}. Me contactaba porque tengo una duda sobre la cancelación de mi plan.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="md:hidden inline-flex items-center gap-1.5 text-[13px] text-brand-primary font-medium active:opacity-50 transition-opacity"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  ¿Tenés alguna duda? Escribinos
                </a>
                <button
                  type="button"
                  onClick={() =>
                    setShowContactQR(
                      `Buenas, soy ${shortName || ""} de ${companyName || ""}. Me contactaba porque tengo una duda sobre la cancelación de mi plan.`,
                    )
                  }
                  className="hidden md:inline-flex items-center gap-1.5 text-[13px] text-brand-primary font-medium hover:text-brand-primary-hover transition-colors"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  ¿Tenés alguna duda? Escribinos
                </button>
              </div>
            </div>
          );
        })()}

      {/* ── Paused subscription notice ── */}
      {!fechaCancelacion &&
        fechaReactivacion &&
        (() => {
          const currentPlanObj = plans.find((p) => p.id === currentPlan);
          const PAUSE_PLAN_LABELS: Record<string, string> = {
            gratis: "Prueba",
            emprendimiento: "Básico",
            negocio: "Pro",
            enterprise: "Ultra",
            enterprise1: "Ultra 500",
            enterprise2: "Ultra 1.000",
            enterprise3: "Ultra 2.000",
            experto: "Empresa",
          };
          const planLabel = PAUSE_PLAN_LABELS[currentPlan] || nombreDelPlanDeEquipo(currentPlan) || currentPlan;
          const reactivacionDateStr = new Date(
            fechaReactivacion,
          ).toLocaleDateString("es-AR", {
            day: "numeric",
            month: "long",
            year: "numeric",
          });
          // Reanudar antes de tiempo: Rebill siempre; MP solo si la pausa la
          // hizo el payment server (status='paused' — las pausas viejas de MP
          // vía n8n dejan el status del preapproval y no son reanudables acá).
          // Stripe (u otra) NO — el payment server no la soporta.
          const canResume =
            plataformaPago === "rebill" ||
            (plataformaPago === "mp" && companyStatus === "paused");
          const pausePlanPrice = currentPlanObj
            ? formatPrice(
                getPlanPrice(currentPlanObj.id, currentPlanObj.price),
              )
            : null;
          return (
            <div className="max-w-[420px] mx-auto mt-2">
              {/* Grouped card container */}
              <div className="bg-surface-card rounded-[13px] overflow-hidden">
                {/* Plan header row */}
                <div className="px-4 py-3.5 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-sm bg-brand-primary/10 flex items-center justify-center">
                      <span className="material-symbols-rounded text-brand-primary text-[17px]">
                        workspace_premium
                      </span>
                    </div>
                    <div>
                      <p className="text-[15px] font-semibold text-brand-black/90">
                        Plan {planLabel}
                      </p>
                      {currentPlanObj && (
                        <p className="text-[13px] text-neutral-500">
                          {formatPrice(
                            getPlanPrice(
                              currentPlanObj.id,
                              currentPlanObj.price,
                            ),
                          )}
                          /mes
                        </p>
                      )}
                    </div>
                  </div>
                  <span className="text-[11px] font-medium text-blue-600 bg-blue-500/10 px-2 py-0.5 rounded-full">
                    Pausado
                  </span>
                </div>

                {/* Separator */}
                <div className="mx-4 h-px bg-black/[0.06]" />

                {/* Pause info */}
                <div className="px-4 py-3.5">
                  <div className="flex items-start gap-2.5">
                    <div className="w-9 h-9 rounded-sm bg-blue-50 flex items-center justify-center shrink-0">
                      <AlertCircle className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="pt-0.5">
                      <p className="text-[15px] font-medium text-brand-black/90">
                        Suscripción pausada
                      </p>
                      <p className="text-[13px] text-neutral-500 mt-0.5">
                        Tu plan se reanudará el{" "}
                        <strong className="text-brand-black/80">
                          {reactivacionDateStr}
                        </strong>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Separator */}
                <div className="mx-4 h-px bg-black/[0.06]" />

                {/* Info note */}
                <div className="px-4 py-3.5">
                  <p className="text-[13px] text-neutral-500 leading-relaxed">
                    Durante la pausa sigues teniendo acceso a todas las
                    funciones de tu plan, pero no se sumarán imágenes
                    adicionales ni se te cobrará. Tu suscripción se reanudará
                    automáticamente en la fecha indicada.
                  </p>
                </div>

                {/* Reanudar antes de tiempo */}
                {canResume && (
                  <>
                    <div className="mx-4 h-px bg-black/[0.06]" />
                    <div className="px-4 py-3.5">
                      <p className="text-[13px] text-neutral-500 leading-relaxed mb-3">
                        ¿Querés volver antes? Podés reanudar tu suscripción
                        ahora.{" "}
                        <span className="text-brand-black/80 font-medium">
                          {plataformaPago === "rebill"
                            ? `Al reanudar se te debitará ${pausePlanPrice ?? "el precio de tu plan"} en el momento y arranca un nuevo ciclo mensual desde hoy.`
                            : `Al reanudar, el débito de ${pausePlanPrice ?? "tu plan"} se hará en tu próxima fecha de facturación (${getNextRenewalDate()}).`}
                        </span>
                      </p>
                      {resumeDeclined && (
                        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2.5 text-[13px] text-red-700">
                          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>
                            No pudimos procesar el cobro con tu tarjeta
                            guardada. Tu suscripción sigue pausada: podés
                            reintentar o pagar con otra tarjeta.
                          </span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={handleResumeSubscription}
                        disabled={resuming}
                        className="w-full py-2.5 px-5 bg-brand-primary hover:bg-brand-primary-hover text-white text-[14px] font-medium rounded-md shadow-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                      >
                        {resuming ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />{" "}
                            Reanudando…
                          </>
                        ) : resumeDeclined ? (
                          "Reintentar"
                        ) : (
                          "Reanudar suscripción"
                        )}
                      </button>
                      {resumeDeclined && (
                        <button
                          type="button"
                          onClick={() =>
                            // Pagar el plan actual con otra tarjeta vía el
                            // checkout embebido: crea la sub nueva y el webhook
                            // activa el plan + da de baja la pausada.
                            navigate("/checkout", {
                              state: { planId: currentPlan, pais, currentPlan },
                            })
                          }
                          className="mt-2 w-full py-2.5 px-5 bg-surface-elevated border border-neutral-200 text-brand-black/90 text-[14px] font-medium rounded-md hover:bg-neutral-100 transition-colors"
                        >
                          Usar otra tarjeta
                        </button>
                      )}
                    </div>
                  </>
                )}

                {/* Features (only if paid plan) */}
                {currentPlanObj && (
                  <>
                    <div className="mx-4 h-px bg-black/[0.06]" />
                    <div className="px-4 py-3.5">
                      <p className="text-[11px] font-medium text-neutral-400 uppercase tracking-wider mb-2.5">
                        Tu plan incluye
                      </p>
                      <ul className="space-y-2">
                        {currentPlanObj.features
                          .filter((f) => !f.startsWith("~"))
                          .map((feature, i) => (
                            <li
                              key={i}
                              className="flex items-start text-[13px] text-neutral-600"
                            >
                              {featureIcon(feature, false)}
                              <span>{feature}</span>
                            </li>
                          ))}
                      </ul>
                    </div>
                  </>
                )}
              </div>

              {/* WhatsApp help — mobile: direct link, desktop: QR modal */}
              <div className="mt-4 text-center">
                <a
                  href={`https://wa.me/5491122635392?text=${encodeURIComponent(`Buenas, soy ${shortName || ""} de ${companyName || ""}. Me contactaba porque tengo una duda sobre la pausa de mi plan.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="md:hidden inline-flex items-center gap-1.5 text-[13px] text-brand-primary font-medium active:opacity-50 transition-opacity"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  ¿Tenés alguna duda? Escribinos
                </a>
                <button
                  type="button"
                  onClick={() =>
                    setShowContactQR(
                      `Buenas, soy ${shortName || ""} de ${companyName || ""}. Me contactaba porque tengo una duda sobre la pausa de mi plan.`,
                    )
                  }
                  className="hidden md:inline-flex items-center gap-1.5 text-[13px] text-brand-primary font-medium hover:text-brand-primary-hover transition-colors"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  ¿Tenés alguna duda? Escribinos
                </button>
              </div>
            </div>
          );
        })()}

      {!fechaCancelacion && !fechaReactivacion && (
        <>
          {/* ── Catálogo de planes rediseñado ──
              Figma "Pricing" (node 2044:448). Ver ADR-2026-07-28-rediseno-paywall-catalogo-componentes-presentacionales (rediseño), ADR-2026-07-28-desktop-3-columnas-vez-selector
              (desktop) y ADR-2026-07-28-chrome-paywall-va-anclado-overlay (chrome fijo).

              Va PORTALEADO a `document.body` por el mismo motivo que los modales
              de más abajo: el shell de contenido es `fixed + transform-gpu`, y un
              ancestro con `transform` contiene a sus hijos `fixed` → sin portal el
              overlay quedaría encerrado en el panel y la sidebar seguiría a la
              vista. Ver [[project_fixed_panel_stacking_trap]]. */}
          {createPortal(
            <PricingCatalog
              // En desktop, los tres planes en fila como página (ADR-2026-07-31-desktop-pasa-comparativo-tres-columnas): es la
              // misma tabla que Configuración → Plan, para que el usuario vea lo
              // mismo por los dos caminos. En mobile cae solo a la pantalla oscura
              // de siempre, que abajo de 1024px sigue siendo la buena.
              variant="comparativa"
              // "¿Son un equipo o una agencia?" (ADR-2026-10-27-planes-de-equipo-en-la-pantalla-de-planes-5).
              onVerPlanesDeEquipo={() => navigate("/subscription?equipos=1")}
              // Carrusel lateral con las slides del rubro de la empresa.
              slides={getCarouselSlidesPorLabel(rubro)}
              plans={planViews}
              selected={selectedPlanKey}
              onSelect={setSelectedPlanKey}
              onSubscribe={(key) => handleSubscription(key)}
              processing={processing}
              onClose={() => navigate(-1)}
              error={error}
              ultraTier={{
                index: ultraTierIndex,
                count: ultraTiers.length,
                onChange: setUltraTierIndex,
              }}
              // El plan anual todavía no existe en el backend: sólo lo ven las
              // cuentas dev_tester y por ahora no cambia nada. Ver ADR-2026-07-28-switch-mensual-anual-visual-solo.
              showBillingToggle={isDevTester === true}
              annual={annualBilling}
              onAnnualChange={setAnnualBilling}
              // Flotante de ayuda (arriba a la derecha). En una compu abrimos el
              // modal con QR; en mobile, WhatsApp directo. Mismo criterio que el
              // botón del pie.
              onHelp={() => {
                if (window.matchMedia('(min-width: 1024px)').matches) {
                  setShowContactQR(dudaMensaje);
                } else {
                  window.open(
                    `https://wa.me/5491122635392?text=${encodeURIComponent(dudaMensaje)}`,
                    '_blank',
                    'noopener,noreferrer',
                  );
                }
              }}
              note={
                <div className="flex flex-col items-center gap-4">
                  <p className="max-w-xl">
                    Las suscripciones se renuevan automáticamente cada mes y podés
                    cancelarlas cuando quieras. Al cambiar de plan, el nuevo límite
                    se aplica de inmediato.
                  </p>


                  {/* Ayuda por WhatsApp. Dos variantes, como en el resto de la
                      pantalla (avisos de cancelación/pausa): en mobile el link
                      abre la app directo; en desktop abre el modal con QR, porque
                      wa.me en una compu manda a WhatsApp Web y lo que queremos es
                      que sigan la charla desde el celular. */}
                  <a
                    href={`https://wa.me/5491122635392?text=${encodeURIComponent(dudaMensaje)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-white/30 px-4 py-2 text-white/90 transition-colors active:bg-white/10 lg:hidden"
                  >
                    <Icon name="outline/whatsapp" size={16} className="shrink-0" />
                    ¿Tenés alguna duda? Escribinos
                  </a>
                  {/* Este mismo `note` lo renderizan las DOS presentaciones: la
                      pantalla oscura de mobile y la página clara de desktop
                      (ADR-2026-07-31-desktop-pasa-comparativo-tres-columnas). Por eso los colores van por breakpoint y no
                      sueltos: en blanco sobre blanco el botón desaparecía. `lg` es
                      el mismo corte que usa `useIsDesktop` para elegir una u otra. */}
                  <button
                    type="button"
                    onClick={() => setShowContactQR(dudaMensaje)}
                    className="hidden items-center gap-2 rounded-full border border-neutral-300 px-4 py-2 text-neutral-600 transition-colors hover:bg-black/[0.04] lg:inline-flex"
                  >
                    <Icon name="outline/whatsapp" size={16} className="shrink-0" />
                    ¿Tenés alguna duda? Escribinos
                  </button>
                </div>
              }
            />,
            document.body,
          )}

          {/* Cancel Subscription Multi-Step Modal — portaleado a document.body para
              que el overlay/blur tape TAMBIÉN la sidebar (el shell de contenido es
              fixed + transform-gpu → contiene los `fixed` hijos). Ver [[project_fixed_panel_stacking_trap]].

              z-[210]: por encima del catálogo (z-[200]), que también es un portal a
              <body>. Con z-50 el modal quedaba DEBAJO y "Cancelar suscripción"
              parecía no hacer nada. La escala entera (catálogo 200 < modales 210 <
              panel dev 230) subió en
              ADR-2026-08-04-el-catalogo-pinta-sobre-las-pantallas-de-modo. */}
          {showCancelModal &&
            createPortal(
            <div className="fixed inset-0 z-[210]">
              {/* Mobile: solid bg / Desktop: dark translucent overlay */}
              <div
                className="absolute inset-0 bg-white md:bg-black/50 md:backdrop-blur-sm"
                onClick={handleCloseCancelModal}
              />
              {/* Scrollable content layer */}
              <div className="relative z-10 flex items-start md:items-center justify-center w-full h-full md:p-4 overflow-y-auto">
                <div
                  className={`${
                    cancelStep === "pause"
                      ? "bg-white md:bg-white md:min-h-[600px]"
                      : cancelStep === 4 || cancelStep === "paused"
                        ? "bg-white md:bg-surface-card md:min-h-[500px]"
                        : "bg-white md:bg-surface-card md:min-h-[420px]"
                  } md:rounded-lg md:shadow-md md:max-w-4xl w-full relative min-h-screen md:min-h-0 md:my-0 pt-12 md:pt-0 flex flex-col`}
                >
                  {/* Close button (hidden on success steps) */}
                  {cancelStep !== 4 && cancelStep !== "paused" && (
                    <button
                      onClick={handleCloseCancelModal}
                      className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 transition-colors z-10"
                      aria-label="Cerrar"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}

                  {/* ── Step 1: Reason selection ── */}
                  {cancelStep === 1 && (
                    <div className="px-4 md:px-5 pb-5 flex flex-col flex-1">
                      <div className="pt-10 md:pt-12 pb-8 md:pb-10 text-center shrink-0">
                        <p className="text-[17px] font-semibold text-brand-black/90 mb-2">
                          Lamentamos que te tengas que ir
                        </p>
                        <p className="text-[13px] text-neutral-500 leading-relaxed">
                          Quisiéramos entender por qué te vas para mejorar la
                          plataforma y que cumpla y supere tus expectativas.
                        </p>
                      </div>
                      <div className="flex-1 flex flex-col justify-center py-3 space-y-2">
                        {cancelReasons.map((reason, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => {
                              setCancelMotivo(reason);
                              setCancelStep("pause");
                            }}
                            className="w-full flex items-center justify-between px-4 md:px-5 py-3.5 text-left bg-surface-card rounded-md border border-neutral-200/70 hover:bg-surface-elevated active:bg-surface-elevated transition-colors"
                          >
                            <span className="text-[15px] text-brand-black/90 pr-3">
                              {reason}
                            </span>
                            <ChevronRight className="w-4 h-4 text-neutral-300 shrink-0" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Step "pause": Offer to pause instead of cancel ── */}
                  {cancelStep === "pause" && (
                    <div className="p-5 flex flex-col flex-1">
                      <button
                        type="button"
                        onClick={() => setCancelStep(1)}
                        disabled={pausingSubscription}
                        className="hidden md:flex text-[13px] text-brand-primary font-medium mb-2 items-center gap-0.5 hover:text-brand-primary-hover transition-colors disabled:opacity-50"
                      >
                        <ChevronRight className="w-3.5 h-3.5 rotate-180" />
                        Volver
                      </button>
                      <p className="text-[17px] font-semibold text-brand-black/90 mb-4 text-center shrink-0">
                        ¿Necesitás un descanso?
                      </p>
                      <div className="flex-1 flex flex-col md:flex-row items-center md:items-center gap-4 md:gap-8 min-h-0">
                        {/* Image */}
                        <div className="w-full md:flex-1 min-h-0 flex items-center justify-center">
                          <img
                            src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/ilustracion_tomando_descanso.png"
                            alt="Tomando un descanso"
                            className="max-h-64 md:max-h-80 max-w-full object-contain"
                          />
                        </div>
                        {/* Title + bullets — to the right of the image on desktop */}
                        <div className="w-full md:flex-1 md:pr-8 flex flex-col text-center md:text-left">
                          <p className="text-[15px] font-medium text-brand-black/90 mb-2 md:mb-3 shrink-0">
                            Pausá tu suscripción de Atlas
                          </p>
                          <ul className="text-[13px] text-neutral-500 leading-relaxed shrink-0 space-y-1.5 text-left w-full">
                            <li className="flex items-start gap-2">
                              <span className="mt-1.5 w-1 h-1 rounded-full bg-neutral-400 shrink-0" />
                              <span>
                                Vas a poder seguir utilizando todas las
                                funciones de tu plan
                              </span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="mt-1.5 w-1 h-1 rounded-full bg-neutral-400 shrink-0" />
                              <span>
                                No se sumarán imágenes adicionales ni se te
                                cobrará nada
                              </span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="mt-1.5 w-1 h-1 rounded-full bg-neutral-400 shrink-0" />
                              <span>
                                Al terminar el período, tu suscripción se
                                reanudará automáticamente
                              </span>
                            </li>
                          </ul>
                          <div className="flex flex-col gap-2.5 pt-5">
                            <button
                              type="button"
                              onClick={() => handlePauseSubscription(1)}
                              disabled={pausingSubscription}
                              className="h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors flex items-center justify-center disabled:opacity-60"
                            >
                              {pausingSubscription &&
                              pauseMonthsSelected === 1 ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                "Pausar por 1 mes"
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => handlePauseSubscription(2)}
                              disabled={pausingSubscription}
                              className="h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors flex items-center justify-center disabled:opacity-60"
                            >
                              {pausingSubscription &&
                              pauseMonthsSelected === 2 ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                "Pausar por 2 meses"
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="pt-4">
                        <button
                          type="button"
                          onClick={() =>
                            setCancelStep(canOfferDowngrade ? "downgrade" : 2)
                          }
                          disabled={pausingSubscription}
                          className="w-full h-10 px-4 border border-neutral-200 rounded-md shadow-sm bg-surface-card text-[15px] font-medium text-brand-black/90 hover:bg-neutral-100 transition-colors disabled:opacity-60"
                        >
                          Cancelar suscripción
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── Step "downgrade": Offer a cheaper plan before cancelling ── */}
                  {cancelStep === "downgrade" && (
                    <div className="p-5 flex flex-col flex-1">
                      <button
                        type="button"
                        onClick={() => setCancelStep("pause")}
                        className="hidden md:flex text-[13px] text-brand-primary font-medium mb-2 items-center gap-0.5 hover:text-brand-primary-hover transition-colors"
                      >
                        <ChevronRight className="w-3.5 h-3.5 rotate-180" />
                        Volver
                      </button>
                      <div className="pt-5 md:pt-2 pb-6 text-center shrink-0">
                        <div className="mx-auto w-12 h-12 rounded-md bg-green-50 border border-green-200 flex items-center justify-center mb-4">
                          <TrendingDown className="w-5 h-5 text-green-600" />
                        </div>
                        <p className="text-[17px] font-semibold text-brand-black/90 mb-2">
                          ¿Y si pagás menos en vez de cancelar?
                        </p>
                        <p className="text-[13px] text-neutral-500 leading-relaxed max-w-md mx-auto">
                          Bajando de plan seguís usando Atlas todos los meses,
                          pagando menos.
                        </p>
                      </div>
                      <div className="flex-1 flex flex-col justify-center space-y-2 max-w-xl w-full mx-auto">
                        {downgradeOptions.map((p) => {
                          const saving =
                            planPriceById(currentPlan) - planPriceById(p.id);
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => {
                                handleCloseCancelModal();
                                handleSubscription(p.id);
                              }}
                              className="w-full flex items-center justify-between gap-3 px-4 md:px-5 py-3.5 text-left bg-surface-card rounded-md border border-neutral-200/70 hover:bg-surface-elevated active:bg-surface-elevated transition-colors"
                            >
                              <span className="min-w-0">
                                <span className="block text-[15px] font-medium text-brand-black/90">
                                  Plan {p.name}
                                </span>
                                <span className="block text-[13px] text-neutral-500">
                                  {formatPrice(planPriceById(p.id))} /mes
                                </span>
                              </span>
                              <span className="flex items-center gap-2 shrink-0">
                                <span className="px-2.5 py-1 rounded-sm bg-green-50 border border-green-200 text-[13px] font-semibold text-green-700 whitespace-nowrap">
                                  Ahorrás {formatPrice(saving)}/mes
                                </span>
                                <ChevronRight className="w-4 h-4 text-neutral-300" />
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      <div className="pt-4 max-w-xl w-full mx-auto">
                        <button
                          type="button"
                          onClick={() => setCancelStep(2)}
                          className="w-full h-10 px-4 border border-neutral-200 rounded-md shadow-sm bg-surface-card text-[15px] font-medium text-brand-black/90 hover:bg-neutral-100 transition-colors"
                        >
                          Continuar con la cancelación
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── Step 2: Detail text ── */}
                  {cancelStep === 2 && (
                    <div className="p-5 flex flex-col flex-1">
                      <div>
                        <button
                          type="button"
                          onClick={() => {
                            setCancelStep(
                              canOfferDowngrade ? "downgrade" : "pause",
                            );
                            setCancelDetalle("");
                          }}
                          className="hidden md:flex text-[13px] text-brand-primary font-medium mb-4 items-center gap-0.5 hover:text-brand-primary-hover transition-colors"
                        >
                          <ChevronRight className="w-3.5 h-3.5 rotate-180" />
                          Volver
                        </button>
                        <p className="text-[17px] font-semibold text-brand-black/90 mb-1">
                          ¿Podrías contarnos por qué?
                        </p>
                        <p className="text-[13px] text-neutral-500 mb-4">
                          Motivo:{" "}
                          <span className="font-medium text-brand-black/70">
                            {cancelMotivo}
                          </span>
                        </p>
                        <textarea
                          value={cancelDetalle}
                          onChange={(e) => setCancelDetalle(e.target.value)}
                          placeholder="Contanos un poco más..."
                          className="w-full h-32 px-3 py-2.5 text-[15px] border border-neutral-200 rounded-md bg-surface-card focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors resize-none placeholder:text-neutral-400"
                          autoFocus
                        />
                      </div>
                      <button
                        onClick={() => setCancelStep(3)}
                        disabled={cancelDetalle.trim().length === 0}
                        className={`w-full mt-auto h-10 px-4 rounded-md text-[15px] font-medium shadow-sm transition-colors flex items-center justify-center ${
                          cancelDetalle.trim().length > 0
                            ? "bg-brand-primary hover:bg-brand-primary-hover text-white"
                            : "bg-neutral-100 text-neutral-400 cursor-not-allowed"
                        }`}
                      >
                        Siguiente
                      </button>
                    </div>
                  )}

                  {/* ── Step 3: Final confirmation ── */}
                  {cancelStep === 3 && (
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex-1 flex items-center justify-center">
                        <div className="text-center">
                          <div className="mx-auto w-12 h-12 rounded-md bg-red-50 border border-red-200 flex items-center justify-center mb-4">
                            <Ban className="w-5 h-5 text-red-600" />
                          </div>
                          <h3 className="text-[17px] font-semibold text-brand-black/90 mb-2">
                            ¿Estás seguro?
                          </h3>
                          <p className="text-[15px] text-neutral-500">
                            Al confirmar, tu suscripción se cancelará y perderás
                            acceso a las funciones premium al finalizar tu
                            período actual.
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2.5 pt-4">
                        <button
                          onClick={handleCancelSubscription}
                          disabled={cancellingSubscription}
                          className="h-10 px-4 bg-red-600 hover:bg-red-700 text-white rounded-md text-[15px] font-medium shadow-sm transition-colors flex items-center justify-center"
                        >
                          {cancellingSubscription ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            "Cancelar suscripción"
                          )}
                        </button>
                        <button
                          onClick={() => setCancelStep(2)}
                          disabled={cancellingSubscription}
                          className="h-10 px-4 border border-neutral-200 rounded-md shadow-sm bg-surface-card text-[15px] font-medium text-brand-black/90 hover:bg-neutral-100 transition-colors"
                        >
                          Volver
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── Step 4: Success ── */}
                  {cancelStep === 4 && (
                    <div className="p-5 md:p-8 flex flex-col flex-1 text-center">
                      <div className="flex-1 flex flex-col justify-center items-center max-w-md mx-auto">
                        <div className="w-12 h-12 rounded-md bg-green-50 border border-green-200 flex items-center justify-center mb-4">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        </div>
                        <h3 className="text-[17px] md:text-[19px] font-semibold text-brand-black/90 mb-2">
                          Cancelación exitosa
                        </h3>
                        <p className="text-[15px] text-neutral-500 mb-1">
                          Tu suscripción ha sido cancelada.
                        </p>
                        <p className="text-[13px] text-neutral-500 leading-relaxed">
                          Podrás seguir usando todas las funciones de tu plan
                          actual hasta el{" "}
                          <strong className="text-brand-black/80">
                            {cancelSuccessDate}
                          </strong>
                          .
                        </p>
                      </div>
                      <div className="mt-auto pt-5 md:pt-8 flex justify-center shrink-0">
                        <button
                          onClick={() => {
                            handleCloseCancelModal();
                            fetchUserProfile();
                          }}
                          className="w-full md:w-auto md:min-w-[240px] h-10 px-6 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
                        >
                          Entendido
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── Step "paused": Pause success ── */}
                  {cancelStep === "paused" && (
                    <div className="p-5 md:p-8 flex flex-col flex-1 text-center">
                      <div className="flex-1 flex flex-col justify-center items-center max-w-md mx-auto">
                        <div className="w-12 h-12 rounded-md bg-green-50 border border-green-200 flex items-center justify-center mb-4">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        </div>
                        <h3 className="text-[17px] md:text-[19px] font-semibold text-brand-black/90 mb-2">
                          Suscripción pausada
                        </h3>
                        <p className="text-[13px] text-neutral-500 leading-relaxed">
                          Tu suscripción fue pausada. Vas a poder seguir
                          utilizando Atlas y no se te cobrará nada por los
                          próximos{" "}
                          <strong className="text-brand-black/80">
                            {pauseMonthsSelected === 1
                              ? "1 mes"
                              : `${pauseMonthsSelected} meses`}
                          </strong>
                          . Igualmente, podés reactivar tu suscripción cuando
                          quieras antes de esa fecha.
                        </p>
                      </div>
                      <div className="mt-auto pt-5 md:pt-8 flex justify-center shrink-0">
                        <button
                          onClick={() => {
                            handleCloseCancelModal();
                            fetchUserProfile();
                          }}
                          className="w-full md:w-auto md:min-w-[240px] h-10 px-6 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[15px] font-medium shadow-sm transition-colors"
                        >
                          Entendido
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>,
            document.body,
          )}
        </>
      )}

      {/* Contact QR Modal — portaleado a document.body (mismo motivo que el modal de
          cancelación: si no, el overlay no tapa la sidebar). */}
      {showContactQR &&
        createPortal(
        <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm flex items-center justify-center z-[210] p-4">
          <div className="bg-surface-card rounded-lg shadow-md max-w-sm w-full p-5 relative">
            <button
              onClick={() => setShowContactQR("")}
              className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-[17px] font-semibold text-brand-black/90 mb-1.5 text-center">
              Contactanos por WhatsApp
            </h3>
            <p className="text-[13px] text-neutral-500 mb-4 text-center">
              Escaneá el código QR con tu celular para abrir WhatsApp
            </p>
            <div className="flex justify-center mb-4">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`https://wa.me/5491122635392?text=${encodeURIComponent(showContactQR)}`)}`}
                alt="QR Code WhatsApp"
                className="w-48 h-48 rounded-md"
              />
            </div>
            <a
              href={`https://wa.me/5491122635392?text=${encodeURIComponent(showContactQR)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full h-10 inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1fb855] text-white rounded-md font-medium text-[15px] shadow-sm transition-colors mb-2"
            >
              Abrir WhatsApp Web
              <ExternalLink className="w-4 h-4" />
            </a>
            <button
              onClick={() => setShowContactQR("")}
              className="w-full h-10 px-4 bg-surface-elevated border border-neutral-200 text-brand-black/90 rounded-md text-[15px] font-medium hover:bg-neutral-100 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

/** Guard de dueño (spec 2026-09-11-invitaciones-equipo §4.3): un miembro no paga ni cambia el plan.
 *  Un guard acá cubre todos los CTAs de "mejorar plan" de la app sin tocar cada uno. */
export const Paywall = () => {
  const cuenta = useCuentaAtlas();
  if (cuenta.cargando) return null;
  // Con facturación por marca no se vende un plan de empresa: el plan de la marca activa (un
  // miembro ve su "Pedile al admin"). Sin bandera, lo de siempre. ADR-2026-10-29-paywall-con-planes-por-marca.
  return (
    <PuertaDelPlanDeEmpresa>
      {cuenta.esDueno ? <PaywallDelDueno /> : <SoloElDueno />}
    </PuertaDelPlanDeEmpresa>
  );
};

export default Paywall;
